import { PCMP_EDIT_LIMITS } from '../constants/pcmpProposal.constants';
import type {
  PcmpAccountEditPayload,
  PcmpAccountKind,
  PcmpPaymentAccount,
} from '../types/pcmpProposal.types';
import { blankToNull, isBdMobile, pcmpOptionalTextSchema } from './pcmpProposalEditForm';

// The teacher's bank or wallet details, shared by the payment edit and the
// correction of a failed payment (LB-764).

export type PcmpAccountField =
  | 'holderName'
  | 'bankName'
  | 'branchName'
  | 'accountNo'
  | 'routingNo'
  | 'mobileNo';

export type PcmpAccountValues = Record<PcmpAccountField, string>;

export const PCMP_ACCOUNT_FIELD_NAMES: readonly PcmpAccountField[] = [
  'holderName',
  'bankName',
  'branchName',
  'accountNo',
  'routingNo',
  'mobileNo',
];

const shortText = pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.shortText);

/** The form fields' zod shape; completeness is checked by `buildPcmpAccountPayload`. */
export const pcmpAccountFormShape = {
  holderName: pcmpOptionalTextSchema(PCMP_EDIT_LIMITS.holderName),
  bankName: shortText,
  branchName: shortText,
  accountNo: shortText,
  routingNo: shortText,
  mobileNo: shortText,
};

export const PCMP_ACCOUNT_FIELDS_BY_KIND: Record<PcmpAccountKind, PcmpAccountField[]> = {
  BANK: ['holderName', 'bankName', 'branchName', 'accountNo', 'routingNo'],
  MFS: ['mobileNo', 'holderName'],
};

export const PCMP_ACCOUNT_REQUIRED_BY_KIND: Record<PcmpAccountKind, PcmpAccountField[]> = {
  BANK: ['holderName', 'bankName', 'branchName', 'accountNo', 'routingNo'],
  MFS: ['mobileNo'],
};

export const PCMP_ACCOUNT_LABELS: Record<PcmpAccountField, string> = {
  holderName: 'Account holder',
  bankName: 'Bank',
  branchName: 'Branch',
  accountNo: 'Account no',
  routingNo: 'Routing no',
  mobileNo: 'Wallet number',
};

export function pcmpAccountDefaults(account: PcmpPaymentAccount | null): PcmpAccountValues {
  return {
    holderName: account?.holderName ?? '',
    bankName: account?.bankName ?? '',
    branchName: account?.branchName ?? '',
    accountNo: account?.accountNo ?? '',
    routingNo: account?.routingNo ?? '',
    mobileNo: account?.mobileNo ?? '',
  };
}

export function isPcmpAccountChanged(
  kind: PcmpAccountKind,
  values: PcmpAccountValues,
  saved: PcmpAccountValues,
): boolean {
  return PCMP_ACCOUNT_FIELDS_BY_KIND[kind].some(
    (field) => values[field].trim() !== saved[field].trim(),
  );
}

/**
 * The account as the API takes it: complete for its kind, or null when a
 * field is missing or malformed (each error is reported through `setError`).
 */
export function buildPcmpAccountPayload(
  kind: PcmpAccountKind,
  values: PcmpAccountValues,
  setError: (field: PcmpAccountField, message: string) => void,
): PcmpAccountEditPayload | null {
  let isValid = true;
  for (const field of PCMP_ACCOUNT_REQUIRED_BY_KIND[kind]) {
    if (values[field] === '') {
      setError(field, `${PCMP_ACCOUNT_LABELS[field]} is required`);
      isValid = false;
    }
  }
  if (kind === 'BANK' && values.routingNo !== '' && !/^\d{9}$/.test(values.routingNo)) {
    setError('routingNo', 'Routing no is exactly 9 digits');
    isValid = false;
  }
  if (kind === 'MFS' && values.mobileNo !== '' && !isBdMobile(values.mobileNo)) {
    setError('mobileNo', 'Enter a valid mobile number, e.g. 01711000000');
    isValid = false;
  }
  if (!isValid) return null;

  return Object.fromEntries(
    PCMP_ACCOUNT_FIELDS_BY_KIND[kind].map((field) => [field, blankToNull(values[field])]),
  );
}
